/**
 * The request list (landing view after sign-in): MainScreen's gallery. The server applies visibility, the tab and
 * the starts-with search (GET /api/requests); this view only draws what comes back. Cancelled requests never come
 * back. The tab and search survive a round trip to a request and back, as the canvas variables did.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, listRequests, type ListFilter, type ListRow } from './api';
import type { App } from './app';
import { day, statusPill } from './format';

const state = {
  filter: 'active' as ListFilter,
  q: '',
  rows: [] as ListRow[],
  loading: false,
  error: '',
  seq: 0,
};
let debounce: number | undefined;

async function load(app: App): Promise<void> {
  const seq = ++state.seq;
  state.loading = true;
  state.error = '';
  app.redraw();
  try {
    // The canvas: Trim(search) = "" -> no filter; otherwise StartsWith(Title, search) on the text as typed.
    const rows = await listRequests(state.filter, state.q.trim() ? state.q : '');
    if (seq !== state.seq) return; // a newer request superseded this one
    state.rows = rows;
  } catch (e) {
    if (seq !== state.seq) return;
    state.error = e instanceof ApiError ? e.message : (e as Error).message || 'The request list could not be loaded.';
  } finally {
    if (seq === state.seq) {
      state.loading = false;
      app.redraw();
    }
  }
}

export function enterList(app: App): void {
  void load(app);
}

function setFilter(app: App, f: ListFilter): void {
  if (state.filter === f) return;
  state.filter = f;
  void load(app);
}

function onSearch(app: App, e: Event): void {
  state.q = (e.target as HTMLInputElement).value;
  window.clearTimeout(debounce);
  debounce = window.setTimeout(() => void load(app), 250);
}

const TABS: [ListFilter, string][] = [
  ['active', 'Active'],
  ['completed', 'Completed'],
  ['all', 'All'],
];

function empty(): string {
  if (state.q.trim()) return 'No requests match your search.';
  return state.filter === 'active' ? 'No active requests.' : state.filter === 'completed' ? 'No completed requests.' : 'No requests found.';
}

function row(r: ListRow): TemplateResult {
  const when = r.effectiveDate ? `${r.requestType === 'Onboarding' ? 'Starts' : 'Last day'} ${day(r.effectiveDate)}` : '';
  return html`
    <a class="row req" href="#/requests/${r.id}">
      <span class="ic" aria-hidden="true">${r.requestType === 'Onboarding' ? '+' : '−'}</span>
      <span class="t">
        <b>${r.displayName}</b>
        <span class="meta">
          <span class="tag">${r.requestType}</span><span class="tag">${r.personType}</span>
          <span>${r.department}</span>${when ? html`<span>${when}</span>` : nothing}
        </span>
        <span class="sub">Manager ${r.managerEmail}</span>
      </span>
      ${statusPill(r.overallStatus)}
    </a>`;
}

export function listView(app: App): TemplateResult {
  return html`
    <main id="main" class="wrap">
      <div class="hello">
        <h1>Requests</h1>
        <div class="acts">
          <a class="btn" href="#/new">
            <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
            New onboarding
          </a>
          <a class="btn ghost" href="#/new-offboarding">
            <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg>
            New offboarding
          </a>
        </div>
      </div>
      <div class="toolbar">
        <div class="tabs" role="group" aria-label="Show">
          ${TABS.map(
            ([f, label]) =>
              html`<button type="button" class="chip" aria-pressed=${state.filter === f ? 'true' : 'false'} @click=${() => setFilter(app, f)}>${label}</button>`,
          )}
        </div>
        <label class="find">
          <span class="sr-only">Search by name</span>
          <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" placeholder="Search by name" .value=${state.q} @input=${(e: Event) => onSearch(app, e)} autocomplete="off">
        </label>
      </div>
      ${state.error
        ? html`<div class="notice err" role="alert">${state.error} <button class="mini" type="button" @click=${() => load(app)}>Try again</button></div>`
        : nothing}
      <div class="list" aria-busy=${state.loading ? 'true' : 'false'}>
        ${state.rows.length
          ? state.rows.map(row)
          : html`<p class="list-empty">${state.loading ? 'Loading…' : empty()}</p>`}
      </div>
    </main>`;
}
